import { HttpErrorResponse } from '@angular/common/http';

// Assumed error envelope — no backend contract specifies the exact shape yet,
// only status + a suggested code per case. Every domain's MSW handlers emit
// this same shape so dev/test stay consistent until a real contract lands.
export interface ApiErrorBody {
  error: {
    code: string;
    message?: string;
    fieldErrors?: Record<string, string[]>;
  };
}

export class ApiError<TCode extends string = string> extends Error {
  constructor(
    readonly code: TCode,
    readonly status: number,
    message: string,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
  }
}

type ApiErrorConstructor<TCode extends string, TError extends ApiError<string>> = new (
  code: TCode,
  status: number,
  message: string,
  fieldErrors?: Record<string, string[]>,
) => TError;

// Shared by every domain service's catchError: pulls the code/message/field
// errors out of the assumed envelope above, falling back to a domain-supplied
// code when the body doesn't match. Pass a subclass (e.g. RegistrationApiError,
// whose code type is the union of every error enum it can throw) to preserve
// that type at the throw site; omit it to get a plain ApiError<TCode>.
export function toApiError<TCode extends string>(response: HttpErrorResponse, fallbackCode: TCode): ApiError<TCode>;
export function toApiError<TCode extends string, TError extends ApiError<string>>(
  response: HttpErrorResponse,
  fallbackCode: TCode,
  ErrorClass: ApiErrorConstructor<TCode, TError>,
): TError;
export function toApiError(
  response: HttpErrorResponse,
  fallbackCode: string,
  ErrorClass: ApiErrorConstructor<string, ApiError<string>> = ApiError,
): ApiError<string> {
  const body = response.error as ApiErrorBody | undefined;
  const code = body?.error?.code ?? fallbackCode;
  const message = body?.error?.message ?? 'Ocurrió un error inesperado.';
  return new ErrorClass(code, response.status, message, body?.error?.fieldErrors);
}
